import React, { useState, useEffect, useRef } from 'react';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Plus, Edit2, Trash2, Image, Upload, Link, X, CheckCircle2, FileSpreadsheet, Download, Sparkles, Square, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import { PictureImagesPanel } from '../../components/PictureImagesPanel';

// Helper to resolve image URLs (handles both relative and absolute URLs)
const resolveImageUrl = (url) => {
  if (!url) return null;
  // If already absolute URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // For relative URLs, prepend the backend URL
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  return `${backendUrl}${url}`;
};

export function AdminImageQuizPage() {
  const [questions, setQuestions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [panelKey, setPanelKey] = useState(0);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    topic_id: '',
    text_en: '',
    text_sk: '',
    image_url: '',
    options: [
      { key: 'A', label_en: '', label_sk: '' },
      { key: 'B', label_en: '', label_sk: '' },
      { key: 'C', label_en: '', label_sk: '' },
      { key: 'D', label_en: '', label_sk: '' }
    ],
    correct_key: 'A',
    active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [questionsRes, topicsRes] = await Promise.all([
        adminAPI.getQuestions(),
        adminAPI.getTopics()
      ]);
      // Filter to show only questions with images
      const imageQuestions = questionsRes.data.questions.filter(q => q.image_url);
      setQuestions(imageQuestions);
      setTopics(topicsRes.data.topics);
      setSelectedIds(new Set()); // Clear selection on reload
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === questions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(questions.map(q => q._id)));
    }
  };

  const toggleSelect = (questionId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(questionId)) {
      newSelected.delete(questionId);
    } else {
      newSelected.add(questionId);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      toast.error('No questions selected');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} questions? This cannot be undone.`)) {
      return;
    }
    
    setBulkDeleting(true);
    let deleted = 0;
    let errors = 0;
    
    for (const questionId of selectedIds) {
      try {
        await adminAPI.deleteQuestion(questionId);
        deleted++;
      } catch (error) {
        errors++;
      }
    }
    
    setBulkDeleting(false);
    setSelectedIds(new Set());
    
    if (deleted > 0) {
      toast.success(`Deleted ${deleted} questions`);
    }
    if (errors > 0) {
      toast.error(`Failed to delete ${errors} questions`);
    }
    
    loadData();
  };

  const resetForm = () => {
    setFormData({
      topic_id: '',
      text_en: '',
      text_sk: '',
      image_url: '',
      options: [
        { key: 'A', label_en: '', label_sk: '' },
        { key: 'B', label_en: '', label_sk: '' },
        { key: 'C', label_en: '', label_sk: '' },
        { key: 'D', label_en: '', label_sk: '' }
      ],
      correct_key: 'A',
      active: true
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await adminAPI.uploadImage(formDataUpload);
      const imageUrl = response.data.image_url;
      
      // Convert to full URL
      const fullUrl = `${window.location.origin}${imageUrl}`;
      setFormData(prev => ({ ...prev, image_url: fullUrl }));
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateAIImage = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter an image prompt');
      return;
    }
    
    setGeneratingAI(true);
    toast.info('Generating AI image... This may take 30-60 seconds.');
    
    try {
      const response = await adminAPI.generateAIImage(aiPrompt);
      const imageUrl = response.data.image_url;
      
      // Convert to full URL for display
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${backendUrl}${imageUrl}`;
      
      setFormData(prev => ({ ...prev, image_url: fullUrl }));
      setAiPrompt('');
      toast.success('AI image generated successfully!');
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate AI image');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.image_url) {
      toast.error('Please add an image URL or upload an image');
      return;
    }
    
    setSubmitting(true);
    try {
      // Transform form data to match API format
      const apiData = {
        topic_id: formData.topic_id,
        text: {
          en: formData.text_en,
          sk: formData.text_sk || formData.text_en
        },
        options: formData.options.map(opt => ({
          key: opt.key,
          label: {
            en: opt.label_en,
            sk: opt.label_sk || opt.label_en
          }
        })),
        correct_key: formData.correct_key,
        image_url: formData.image_url,
        active: formData.active
      };
      
      await adminAPI.createQuestion(apiData);
      toast.success('Image question created successfully!');
      setCreateOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create question');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const apiData = {
        topic_id: formData.topic_id,
        text: {
          en: formData.text_en,
          sk: formData.text_sk || formData.text_en
        },
        options: formData.options.map(opt => ({
          key: opt.key,
          label: {
            en: opt.label_en,
            sk: opt.label_sk || opt.label_en
          }
        })),
        correct_key: formData.correct_key,
        image_url: formData.image_url,
        active: formData.active
      };
      
      await adminAPI.updateQuestion(selectedQuestion._id, apiData);
      toast.success('Question updated successfully!');
      setEditOpen(false);
      setSelectedQuestion(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update question');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await adminAPI.deleteQuestion(questionId);
      toast.success('Question deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const openEditDialog = (question) => {
    setSelectedQuestion(question);
    
    // Extract text based on structure
    const textEn = typeof question.text === 'object' ? question.text.en : question.text;
    const textSk = typeof question.text === 'object' ? question.text.sk : question.text;
    
    setFormData({
      topic_id: question.topic_id,
      text_en: textEn || '',
      text_sk: textSk || '',
      image_url: question.image_url || '',
      options: question.options.map(opt => ({
        key: opt.key,
        label_en: typeof opt.label === 'object' ? opt.label.en : opt.label,
        label_sk: typeof opt.label === 'object' ? opt.label.sk : opt.label
      })),
      correct_key: question.correct_key,
      active: question.active !== false
    });
    setEditOpen(true);
  };

  const handleDownloadTemplate = async () => {
    try {
      await adminAPI.downloadImageQuizTemplate();
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const [forceUpload, setForceUpload] = useState(false);
  
  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    console.log('Starting bulk upload with file:', file.name, 'Force:', forceUpload);
    setBulkUploading(true);
    
    // The server saves the questions at once; the AI draws the pictures afterwards in the background
    const toastId = toast.loading('Uploading the Excel file...', {
      duration: Infinity
    });
    
    try {
      console.log('Calling bulkUploadImageQuiz API...');
      const response = await adminAPI.bulkUploadImageQuiz(file, forceUpload);
      console.log('Bulk upload response:', response.data);
      
      // Dismiss loading toast
      toast.dismiss(toastId);
      
      const { imported, skipped, errors, message } = response.data;
      
      // Show summary with long duration
      if (imported > 0) {
        toast.success(message || `Added ${imported} questions.`, { duration: 30000 });
        setPanelKey((k) => k + 1);
      }
      
      if (skipped > 0) {
        toast.info(`⏭️ Skipped ${skipped} duplicate questions.`, { duration: 30000 });
      }
      
      if (errors && errors.length > 0) {
        // Show all errors with long duration so user can read them
        toast.error(`❌ ${errors.length} errors occurred:`, { duration: 60000 });
        errors.forEach((err, i) => {
          setTimeout(() => {
            toast.error(`Error ${i + 1}: ${err}`, { duration: 60000 });
          }, (i + 1) * 500); // Stagger error toasts
        });
      }
      
      if (imported === 0 && skipped === 0 && (!errors || errors.length === 0)) {
        toast.warning(message || 'Upload completed but no questions were processed.', { duration: 30000 });
      }
      
      setBulkUploadOpen(false);
      setForceUpload(false);
      loadData();
    } catch (error) {
      toast.dismiss(toastId);
      console.error('Bulk upload error:', error);
      console.error('Error response:', error.response?.data);
      
      // Show detailed error with long duration
      const errorDetail = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`❌ Bulk upload failed: ${errorDetail}`, { duration: 60000 });
    } finally {
      setBulkUploading(false);
      if (bulkFileInputRef.current) {
        bulkFileInputRef.current.value = '';
      }
    }
  };

  const QuestionForm = ({ onSubmit, submitText }) => (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Topic Selection */}
      <div>
        <Label>Topic *</Label>
        <select
          value={formData.topic_id}
          onChange={(e) => setFormData({ ...formData, topic_id: e.target.value })}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          required
        >
          <option value="">Select a topic</option>
          {topics.map((topic) => (
            <option key={topic._id} value={topic._id}>
              {topic.name} ({topic.name_sk || topic.name})
            </option>
          ))}
        </select>
      </div>

      {/* Image Section */}
      <div className="space-y-3">
        <Label className="flex items-center space-x-2">
          <Image className="w-4 h-4" />
          <span>Question Image *</span>
        </Label>
        
        {/* Image Preview */}
        {formData.image_url && (
          <div className="relative inline-block">
            <img 
              src={formData.image_url} 
              alt="Question" 
              className="max-w-full max-h-48 rounded-lg border border-slate-200"
            />
            <button
              type="button"
              onClick={() => setFormData({ ...formData, image_url: '' })}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        
        {/* AI Image Generation */}
        <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-4 mb-3">
          <Label className="flex items-center space-x-2 text-violet-700 mb-2">
            <Sparkles className="w-4 h-4" />
            <span>Generate with AI</span>
          </Label>
          <div className="flex space-x-2">
            <Input
              placeholder="Describe the image you want (e.g., 'Eiffel Tower at sunset')"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              className="flex-1"
              disabled={generatingAI}
            />
            <Button
              type="button"
              onClick={handleGenerateAIImage}
              disabled={generatingAI || !aiPrompt.trim()}
              className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"
            >
              {generatingAI ? (
                <>
                  <LoadingSpinner className="w-4 h-4 mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate
                </>
              )}
            </Button>
          </div>
        </div>
        
        {/* Or divider */}
        <div className="relative my-2">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-slate-200" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-slate-400">Or add manually</span>
          </div>
        </div>
        
        {/* Image URL Input */}
        <div className="flex space-x-2">
          <div className="flex-1 relative">
            <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="url"
              placeholder="Paste image URL here..."
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              className="pl-10"
            />
          </div>
          <span className="text-slate-400 self-center">or</span>
          <div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="border-teal-300 text-teal-700 hover:bg-teal-50"
            >
              {uploading ? (
                <>
                  <LoadingSpinner className="w-4 h-4 mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Question Text */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Question Text (English) *</Label>
          <Input
            value={formData.text_en}
            onChange={(e) => setFormData({ ...formData, text_en: e.target.value })}
            placeholder="What is shown in this image?"
            required
          />
        </div>
        <div>
          <Label>Question Text (Slovak)</Label>
          <Input
            value={formData.text_sk}
            onChange={(e) => setFormData({ ...formData, text_sk: e.target.value })}
            placeholder="Čo je zobrazené na tomto obrázku?"
          />
        </div>
      </div>

      {/* Answer Options */}
      <div className="space-y-3">
        <Label>Answer Options</Label>
        {formData.options.map((option, idx) => (
          <div key={option.key} className="flex items-center space-x-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
              formData.correct_key === option.key 
                ? 'bg-teal-500 text-white' 
                : 'bg-slate-200 text-slate-700'
            }`}>
              {option.key}
            </div>
            <div className="flex-1 grid grid-cols-2 gap-2">
              <Input
                placeholder={`Option ${option.key} (English)`}
                value={option.label_en}
                onChange={(e) => {
                  const newOptions = [...formData.options];
                  newOptions[idx].label_en = e.target.value;
                  setFormData({ ...formData, options: newOptions });
                }}
                required
              />
              <Input
                placeholder={`Option ${option.key} (Slovak)`}
                value={option.label_sk}
                onChange={(e) => {
                  const newOptions = [...formData.options];
                  newOptions[idx].label_sk = e.target.value;
                  setFormData({ ...formData, options: newOptions });
                }}
              />
            </div>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, correct_key: option.key })}
              className={`p-2 rounded-lg transition-colors ${
                formData.correct_key === option.key
                  ? 'bg-teal-100 text-teal-700'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
              title="Mark as correct answer"
            >
              <CheckCircle2 className="w-5 h-5" />
            </button>
          </div>
        ))}
      </div>

      {/* Submit */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button type="submit" disabled={submitting} className="bg-gradient-to-r from-teal-500 to-teal-600">
          {submitting ? 'Saving...' : submitText}
        </Button>
      </div>
    </form>
  );

  if (loading) {
    return <LoadingSpinner className="py-20" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Space_Grotesk']">Image Assessment Questions</h1>
          <p className="text-slate-600 mt-1">Create and manage questions with AI-generated images</p>
        </div>
        <div className="flex space-x-2">
          {selectedIds.size > 0 && (
            <Button 
              onClick={handleBulkDelete}
              variant="outline"
              disabled={bulkDeleting}
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="bulk-delete-btn"
            >
              {bulkDeleting ? (
                <>
                  <LoadingSpinner className="w-4 h-4 mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected ({selectedIds.size})
                </>
              )}
            </Button>
          )}
          <Button 
            onClick={() => setBulkUploadOpen(true)}
            variant="outline"
            className="border-teal-300 text-teal-700 hover:bg-teal-50"
            data-testid="bulk-upload-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Bulk Upload (AI Images)
          </Button>
          <Button 
            onClick={() => { resetForm(); setCreateOpen(true); }}
            className="bg-gradient-to-r from-teal-500 to-teal-600"
            data-testid="create-image-question-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Question
          </Button>
        </div>
      </div>

      <PictureImagesPanel key={panelKey} />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-sm text-slate-600">Total Image Questions</p>
          <p className="text-2xl font-bold text-slate-900">{questions.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-sm text-slate-600">Active</p>
          <p className="text-2xl font-bold text-teal-600">{questions.filter(q => q.active !== false).length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-sm text-slate-600">Selected</p>
          <p className="text-2xl font-bold text-slate-900">{selectedIds.size}</p>
        </div>
      </div>

      {/* Select All / Deselect All */}
      {questions.length > 0 && (
        <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3 border border-slate-200">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-2 text-sm text-slate-700 hover:text-teal-600 transition-colors"
            data-testid="select-all-btn"
          >
            {selectedIds.size === questions.length ? (
              <>
                <CheckSquare className="w-5 h-5 text-teal-600" />
                <span>Deselect All</span>
              </>
            ) : (
              <>
                <Square className="w-5 h-5" />
                <span>Select All ({questions.length})</span>
              </>
            )}
          </button>
          {selectedIds.size > 0 && (
            <span className="text-sm text-slate-500">
              {selectedIds.size} of {questions.length} selected
            </span>
          )}
        </div>
      )}

      {/* Questions Grid */}
      {questions.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
          <Image className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No Image Assessment Questions Yet</h3>
          <p className="text-slate-600 mb-4">Upload an Excel file to auto-generate images with AI, or create assessment questions manually.</p>
          <div className="flex justify-center space-x-3">
            <Button onClick={() => setBulkUploadOpen(true)} variant="outline" className="border-teal-300 text-teal-700">
              <Sparkles className="w-4 h-4 mr-2" />
              Bulk Upload (AI)
            </Button>
            <Button onClick={() => { resetForm(); setCreateOpen(true); }} className="bg-gradient-to-r from-teal-500 to-teal-600">
              <Plus className="w-4 h-4 mr-2" />
              Add Manually
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {questions.map((question) => {
            const topic = topics.find(t => t._id === question.topic_id);
            const questionText = typeof question.text === 'object' ? question.text.en : question.text;
            const isSelected = selectedIds.has(question._id);
            
            return (
              <div 
                key={question._id} 
                className={`bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-all ${
                  isSelected ? 'border-teal-500 ring-2 ring-teal-200' : 'border-slate-200'
                }`}
                data-testid={`image-question-${question._id}`}
              >
                {/* Checkbox + Image */}
                <div className="aspect-video bg-slate-100 relative">
                  {/* Selection Checkbox */}
                  <button
                    onClick={() => toggleSelect(question._id)}
                    className={`absolute top-2 left-2 z-10 w-6 h-6 rounded flex items-center justify-center transition-all ${
                      isSelected 
                        ? 'bg-teal-500 text-white' 
                        : 'bg-white/80 text-slate-400 hover:bg-white hover:text-slate-600'
                    }`}
                    data-testid={`select-question-${question._id}`}
                  >
                    {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                  </button>
                  
                  {question.image_url ? (
                    <img 
                      src={resolveImageUrl(question.image_url)} 
                      alt="Question" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Image className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  {question.active === false && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                      Inactive
                    </div>
                  )}
                </div>
                
                {/* Content */}
                <div className="p-4">
                  <p className="text-sm text-teal-600 font-medium mb-1">{topic?.name || 'Unknown Topic'}</p>
                  <p className="text-slate-900 font-medium line-clamp-2 mb-3">{questionText}</p>
                  
                  {/* Options Preview */}
                  <div className="grid grid-cols-2 gap-1 text-xs mb-3">
                    {question.options.slice(0, 4).map(opt => {
                      const label = typeof opt.label === 'object' ? opt.label.en : opt.label;
                      return (
                        <div 
                          key={opt.key}
                          className={`px-2 py-1 rounded ${
                            opt.key === question.correct_key
                              ? 'bg-teal-100 text-teal-800'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {opt.key}: {label?.substring(0, 15)}{label?.length > 15 ? '...' : ''}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex justify-end space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(question)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(question._id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Image className="w-5 h-5 text-teal-600" />
              <span>Create Image Assessment Question</span>
            </DialogTitle>
          </DialogHeader>
          {QuestionForm({ onSubmit: handleCreate, submitText: "Create Question" })}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Edit2 className="w-5 h-5 text-teal-600" />
              <span>Edit Image Assessment Question</span>
            </DialogTitle>
          </DialogHeader>
          {QuestionForm({ onSubmit: handleEdit, submitText: "Update Question" })}
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkUploadOpen} onOpenChange={setBulkUploadOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Sparkles className="w-5 h-5 text-teal-600" />
              <span>Bulk Upload with AI Image Generation</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-lg p-4">
              <h4 className="font-medium text-teal-900 mb-2">🤖 How it works:</h4>
              <ol className="text-sm text-teal-800 space-y-1 list-decimal list-inside">
                <li>Download the Excel template</li>
                <li>Fill in questions and <strong>image prompts</strong></li>
                <li>Upload the file - AI draws the pictures in the background</li>
              </ol>
            </div>
            
            <div className="flex space-x-3">
              <Button
                onClick={handleDownloadTemplate}
                variant="outline"
                className="flex-1"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>
            
            <div className="border-t pt-4">
              <Label className="text-sm font-medium">Upload Excel File</Label>
              <p className="text-xs text-slate-500 mb-2">
                Each row will generate an AI image based on the "image_prompt" column
              </p>
              
              {/* Force Upload Checkbox */}
              <div className="flex items-center space-x-2 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <input
                  type="checkbox"
                  id="forceUpload"
                  checked={forceUpload}
                  onChange={(e) => setForceUpload(e.target.checked)}
                  className="w-4 h-4 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                />
                <label htmlFor="forceUpload" className="text-sm text-amber-800">
                  <span className="font-medium">Force Upload</span> - Upload all questions even if they already exist (creates duplicates)
                </label>
              </div>
              
              <input
                type="file"
                ref={bulkFileInputRef}
                onChange={handleBulkUpload}
                accept=".xlsx,.xls"
                className="hidden"
              />
              
              <Button
                onClick={() => bulkFileInputRef.current?.click()}
                disabled={bulkUploading}
                className="w-full bg-gradient-to-r from-teal-500 to-teal-600"
              >
                {bulkUploading ? (
                  <>
                    <LoadingSpinner className="w-4 h-4 mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Select Excel File & Generate
                  </>
                )}
              </Button>
            </div>
            
            <div className="text-sm text-slate-600 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              The questions are added at once (hidden until they have a picture). The AI then draws the pictures in the
              background - this can take hours for big files, and you can close this page. Progress is shown under
              "Pictures for picture questions".
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
